import type { ReactNode } from "react";

import { cx } from "class-variance-authority";

import { Section, SectionContent } from "../Section";

import * as styles from "./SummaryCard.css";

interface SummaryCardProps {
    /** One or more `SummaryCardColumn` elements. */
    children: ReactNode;
    /** Stack the columns vertically instead of side by side. */
    vertical?: boolean;
}

export function SummaryCard({ children, vertical = false }: SummaryCardProps) {
    return (
        <Section styleVariant="secondary">
            <SectionContent>
                <div
                    className={cx(
                        styles.columns,
                        vertical && styles.columnsVertical
                    )}
                >
                    {children}
                </div>
            </SectionContent>
        </Section>
    );
}

interface SummaryCardColumnProps {
    heading: ReactNode;
    children: ReactNode;
}

export function SummaryCardColumn({
    heading,
    children,
}: SummaryCardColumnProps) {
    return (
        <div className={styles.column}>
            <h3 className={styles.heading}>{heading}</h3>
            <div className={styles.columnBody}>{children}</div>
        </div>
    );
}

interface SummaryCardIconItemProps {
    icon: ReactNode;
    label: ReactNode;
    value: ReactNode;
    /** `"danger"` tints the icon frame and value red, e.g. a missed due date. */
    tone?: "default" | "danger";
}

/** A row with a framed icon and a stacked label/value pair. */
export function SummaryCardIconItem({
    icon,
    label,
    value,
    tone = "default",
}: SummaryCardIconItemProps) {
    const danger = tone === "danger";

    return (
        <div className={styles.iconItem}>
            <span
                className={cx(
                    styles.iconWrapper,
                    danger && styles.iconWrapperDanger
                )}
            >
                {icon}
            </span>
            <div className={styles.iconItemText}>
                <span className={styles.itemLabel}>{label}</span>
                <span
                    className={cx(
                        styles.itemValue,
                        danger && styles.itemValueDanger
                    )}
                >
                    {value}
                </span>
            </div>
        </div>
    );
}

interface SummaryCardItemProps {
    label: ReactNode;
    value: ReactNode;
}

/** A single line with a bold label and an inline value. */
export function SummaryCardItem({ label, value }: SummaryCardItemProps) {
    return (
        <div className={styles.item}>
            <span className={styles.itemInlineLabel}>{label}</span>
            {" – "}
            {value}
        </div>
    );
}
